import axios from "axios";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import DailyTasksAPI from "../../api/dashboards/DailyTasksAPI";
import { trackDailyTasksAction } from "../../../../analytics/dailyTasksTracking";
import {
  DailyTasksDetailResult,
  DailyTasksFilterOptions,
  DailyTasksListRow,
  DailyTasksSortBy,
  EMPTY_FILE_WORK_SUMMARY,
  EMPTY_MOCKUP_WORK_SUMMARY,
  EMPTY_PO_WORK_SUMMARY,
  EMPTY_PR_WORK_SUMMARY,
  EMPTY_RAW_WORK_SUMMARY,
  EMPTY_PRINT_WORK_SUMMARY,
  EMPTY_PRINT_IN_WORK_SUMMARY,
  EMPTY_PROJECT_WORK_SUMMARY,
  FILE_WORK_STATE_LABELS,
  FileWorkState,
  FileWorkSummary,
  MOCKUP_WORK_BUCKET_OPTIONS,
  MOCKUP_WORK_STATE_LABELS,
  MockupWorkState,
  MockupWorkSummary,
  MockupWorkUrgency,
  PO_WORK_STATE_LABELS,
  PR_WORK_STATE_LABELS,
  PROJECT_WORK_STATE_LABELS,
  PRINT_WORK_STATE_LABELS,
  RAW_WORK_STATE_LABELS,
  PoWorkState,
  PoWorkSummary,
  PrWorkState,
  PrWorkSummary,
  PrintWorkState,
  PrintInWorkState,
  PrintInWorkSummary,
  ProjectWorkSummary,
  ProjectWorkState,
  PRINT_IN_WORK_STATE_LABELS,
  PrintWorkSummary,
  RawWorkState,
  RawWorkSummary,
  isDailyTasksFamilyReady,
} from "../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  DailyTasksFilters,
  defaultDailyTasksFilters,
  toDailyTasksTatBody,
} from "../../../../models/Dashboard/DailyTasks/DailyTasksFilters";
import { SortType } from "../../../../models/SortType";
import {
  DailyTasksTatDrilldownQuery,
  DailyTasksTatDrilldownResult,
  DailyTasksTatMetricId,
  DailyTasksTatSummaryResult,
  defaultDailyTasksTatDrilldownQuery,
} from "../../../../models/Dashboard/DailyTasks/DailyTasksTat";

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string" &&
      data.message
    ) {
      return data.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const triggerCsvDownload = (blob: Blob, filename?: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename || "daily-tasks-tat-export.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const stubListRow = (
  orderId: number,
  productId: number,
): DailyTasksListRow => ({
  order_id: orderId,
  order_type_id: 0,
  order_type: null,
  brand_name: null,
  start_date: null,
  dispatch_date: null,
  product_id: productId,
  product_name: null,
  ordered_quantity: 0,
  customisation_required: 1,
  sales_poc_id: null,
  sales_poc: null,
  pm_poc_id: null,
  pm_poc: null,
  warehouse_id: null,
  days_to_dispatch: null,
  days_to_sla: null,
  stage_tat_hours: null,
  total_tat_hours: null,
  stage_status: "pending",
  mockup_count: 0,
  last_uploaded_at: null,
  last_uploaded_by: null,
  logos_uploaded: false,
  variants_qty: 0,
  mockup_work_state: null,
  sla_clock_start: null,
  sla_target_hours: null,
  sla_elapsed_hours: null,
  sla_status: null,
  sla_delta_hours: null,
  mockup_upload_sla_days: null,
  mockup_approval_sla_days: null,
  mockup_rework_sla_days: null,
  mockup_upload_tat_hours: null,
  mockup_approval_tat_hours: null,
  file_upload_sla_days: null,
  cdr_to_printer_sla_days: null,
  file_upload_tat_hours: null,
  cdr_to_printer_tat_hours: null,
  pr_creation_sla_days: null,
  pr_creation_tat_hours: null,
  po_creation_sla_days: null,
  po_creation_tat_hours: null,
  raw_inward_sla_days: null,
  raw_inward_qc_sla_days: null,
  print_outward_sla_days: null,
  print_inward_sla_days: null,
  print_inward_qc_sla_days: null,
  project_request_sla_days: null,
  project_variants_sla_days: null,
  project_invoice_sla_days: null,
  customer_invoice_present: false,
  customer_invoice_date: null,
  pr_qty: 0,
  created_pr_qty: 0,
  pending_sourcing: false,
  pending_finance: false,
  pending_swageazy: false,
  pr_vendor_name: null,
  first_pr_at: null,
  covering_pr_at: null,
  approved_pr_count: 0,
  issued_po_count: 0,
  sent_po_count: 0,
  first_po_at: null,
  all_po_at: null,
  all_email_at: null,
  printer_name: null,
  printing_type: null,
  printing_cost: null,
  mockups_approved_at: null,
  file_count: 0,
  last_file_at: null,
  po_count: 0,
  received_quantity: null,
  rejected_quantity: null,
  sent_quantity: null,
  ordered_po_quantity: null,
  project_finance_status: null,
  project_finance_approval_date: null,
  project_finance_tat_hours: null,
  comments_count: 0,
  commander_path: "",
});

export class DailyTasksStore {
  dailyTasksAPI: DailyTasksAPI;
  filters: DailyTasksFilters = defaultDailyTasksFilters();
  filterOptions: DailyTasksFilterOptions = {
    order_types: [],
    sales_pocs: [],
    pm_pocs: [],
    warehouses: [],
  };

  filtersLoading = false;
  summaryLoading = false;
  drilldownLoading = false;
  exportLoading = false;
  drawerLoading = false;

  summaryError: string | null = null;
  drilldownError: string | null = null;
  exportError: string | null = null;
  drawerErrorMessage: string | null = null;

  summaryResult: DailyTasksTatSummaryResult | null = null;
  drilldownResult: DailyTasksTatDrilldownResult | null = null;

  drawerOpen = false;
  drawerMetricId: DailyTasksTatMetricId | null = null;
  /** Human card title for GA (e.g. "PRs created"). */
  drawerCardTitle: string | null = null;
  /** Dashboard section for GA (days_ledger, stage_turnaround, activity). */
  drawerCardSection: string | null = null;
  drilldownQuery: DailyTasksTatDrilldownQuery =
    defaultDailyTasksTatDrilldownQuery();

  productDrawerOpen = false;
  selectedRow: DailyTasksListRow | null = null;
  detail: DailyTasksDetailResult | null = null;

  queueLoading = false;
  queueError: string | null = null;
  mockupWork: MockupWorkSummary = EMPTY_MOCKUP_WORK_SUMMARY;
  fileWork: FileWorkSummary = EMPTY_FILE_WORK_SUMMARY;
  prWork: PrWorkSummary = EMPTY_PR_WORK_SUMMARY;
  poWork: PoWorkSummary = EMPTY_PO_WORK_SUMMARY;
  rawWork: RawWorkSummary = EMPTY_RAW_WORK_SUMMARY;
  printWork: PrintWorkSummary = EMPTY_PRINT_WORK_SUMMARY;
  printInWork: PrintInWorkSummary = EMPTY_PRINT_IN_WORK_SUMMARY;
  projectWork: ProjectWorkSummary = EMPTY_PROJECT_WORK_SUMMARY;
  queueRows: DailyTasksListRow[] = [];
  queueTotalCount = 0;

  private summaryRequestId = 0;
  private queueRequestId = 0;
  private drilldownRequestId = 0;
  private detailRequestId = 0;

  constructor() {
    this.dailyTasksAPI = new DailyTasksAPI();
    makeObservable(this, {
      filters: observable,
      filterOptions: observable,
      filtersLoading: observable,
      summaryLoading: observable,
      drilldownLoading: observable,
      exportLoading: observable,
      drawerLoading: observable,
      summaryError: observable,
      drilldownError: observable,
      exportError: observable,
      drawerErrorMessage: observable,
      summaryResult: observable.ref,
      drilldownResult: observable.ref,
      drawerOpen: observable,
      drawerMetricId: observable,
      drawerCardTitle: observable,
      drawerCardSection: observable,
      drilldownQuery: observable.ref,
      productDrawerOpen: observable,
      selectedRow: observable,
      detail: observable,
      queueLoading: observable,
      queueError: observable,
      mockupWork: observable,
      fileWork: observable,
      prWork: observable,
      poWork: observable,
      rawWork: observable,
      printWork: observable,
      printInWork: observable,
      projectWork: observable,
      queueRows: observable.ref,
      queueTotalCount: observable,
      setFilters: action,
      updateFilters: action,
      resetFilters: action,
      fetchFilterOptions: action,
      fetchSummary: action,
      fetchQueue: action,
      selectWorkTile: action,
      changeQueuePage: action,
      changeQueueSort: action,
      openDrawer: action,
      closeDrawer: action,
      fetchDrilldown: action,
      exportCsv: action,
      openProductDrawer: action,
      closeProductDrawer: action,
      fetchDetail: action,
      isFamilyReady: computed,
    });
  }

  get isFamilyReady() {
    return isDailyTasksFamilyReady(this.filters.stage);
  }

  setFilters = (filters: DailyTasksFilters) => {
    this.filters = filters;
  };

  updateFilters = (updater: (prev: DailyTasksFilters) => DailyTasksFilters) => {
    this.filters = updater(this.filters);
  };

  resetFilters = () => {
    this.filters = defaultDailyTasksFilters();
  };

  fetchFilterOptions = async () => {
    try {
      this.filtersLoading = true;
      const options = await this.dailyTasksAPI.getFilterOptions();
      runInAction(() => {
        this.filterOptions = options;
      });
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      runInAction(() => {
        this.filtersLoading = false;
      });
    }
  };

  fetchSummary = async (filters?: DailyTasksFilters) => {
    const requestId = ++this.summaryRequestId;
    try {
      if (filters) {
        this.filters = filters;
      }
      this.closeDrawer({ silent: true });
      this.closeProductDrawer({ silent: true });

      if (!isDailyTasksFamilyReady(this.filters.stage)) {
        this.summaryResult = null;
        this.summaryError = null;
        this.summaryLoading = false;
        this.mockupWork = EMPTY_MOCKUP_WORK_SUMMARY;
        this.fileWork = EMPTY_FILE_WORK_SUMMARY;
        this.prWork = EMPTY_PR_WORK_SUMMARY;
        this.poWork = EMPTY_PO_WORK_SUMMARY;
        this.rawWork = EMPTY_RAW_WORK_SUMMARY;
        this.printWork = EMPTY_PRINT_WORK_SUMMARY;
        this.printInWork = EMPTY_PRINT_IN_WORK_SUMMARY;
        this.projectWork = EMPTY_PROJECT_WORK_SUMMARY;
        this.queueRows = [];
        this.queueTotalCount = 0;
        this.queueError = null;
        return;
      }

      this.summaryLoading = true;
      this.summaryError = null;
      const result = await this.dailyTasksAPI.getTatSummary(
        toDailyTasksTatBody(this.filters),
      );
      if (requestId !== this.summaryRequestId) return;
      runInAction(() => {
        this.summaryResult = result;
      });
    } catch (error) {
      if (requestId !== this.summaryRequestId) return;
      runInAction(() => {
        this.summaryError = getApiErrorMessage(
          error,
          "Failed to fetch daily tasks TAT summary.",
        );
        this.summaryResult = null;
      });
      throw error;
    } finally {
      if (requestId === this.summaryRequestId) {
        runInAction(() => {
          this.summaryLoading = false;
        });
      }
    }
  };

  fetchQueue = async (
    filters?: DailyTasksFilters,
    options?: { refreshCards?: boolean },
  ) => {
    const requestId = ++this.queueRequestId;
    const refreshCards = options?.refreshCards !== false;
    try {
      if (filters) {
        this.filters = filters;
      }
      if (!isDailyTasksFamilyReady(this.filters.stage)) {
        this.mockupWork = EMPTY_MOCKUP_WORK_SUMMARY;
        this.fileWork = EMPTY_FILE_WORK_SUMMARY;
        this.prWork = EMPTY_PR_WORK_SUMMARY;
        this.poWork = EMPTY_PO_WORK_SUMMARY;
        this.rawWork = EMPTY_RAW_WORK_SUMMARY;
        this.printWork = EMPTY_PRINT_WORK_SUMMARY;
        this.printInWork = EMPTY_PRINT_IN_WORK_SUMMARY;
        this.projectWork = EMPTY_PROJECT_WORK_SUMMARY;
        this.queueRows = [];
        this.queueTotalCount = 0;
        this.queueError = null;
        this.queueLoading = false;
        return;
      }

      this.queueLoading = true;
      this.queueError = null;
      const [workSummary, queue] = await Promise.all([
        refreshCards
          ? this.dailyTasksAPI.getQueueSummary(this.filters)
          : Promise.resolve(null),
        this.dailyTasksAPI.getQueue(this.filters),
      ]);
      if (requestId !== this.queueRequestId) return;
      runInAction(() => {
        if (workSummary) {
          this.mockupWork = workSummary.mockup_work;
          this.fileWork = workSummary.file_work;
          this.prWork = workSummary.pr_work;
          this.poWork = workSummary.po_work;
          this.rawWork = workSummary.raw_work;
          this.printWork = workSummary.print_work;
          this.printInWork = workSummary.print_in_work;
          this.projectWork = workSummary.project_work;
        }
        this.queueRows = queue.list;
        this.queueTotalCount = queue.total_count;
      });
    } catch (error) {
      if (requestId !== this.queueRequestId) return;
      runInAction(() => {
        this.queueError = getApiErrorMessage(
          error,
          "Failed to fetch pending daily tasks.",
        );
        this.queueRows = [];
        this.queueTotalCount = 0;
        if (refreshCards) {
          this.mockupWork = EMPTY_MOCKUP_WORK_SUMMARY;
          this.fileWork = EMPTY_FILE_WORK_SUMMARY;
          this.prWork = EMPTY_PR_WORK_SUMMARY;
          this.poWork = EMPTY_PO_WORK_SUMMARY;
          this.rawWork = EMPTY_RAW_WORK_SUMMARY;
          this.printWork = EMPTY_PRINT_WORK_SUMMARY;
          this.printInWork = EMPTY_PRINT_IN_WORK_SUMMARY;
          this.projectWork = EMPTY_PROJECT_WORK_SUMMARY;
        }
      });
      throw error;
    } finally {
      if (requestId === this.queueRequestId) {
        runInAction(() => {
          this.queueLoading = false;
        });
      }
    }
  };

  selectWorkTile = async (
    tile:
      | "all_pending"
      | MockupWorkState
      | FileWorkState
      | PrWorkState
      | PoWorkState
      | RawWorkState
      | PrintWorkState
      | PrintInWorkState
      | ProjectWorkState,
    urgency: MockupWorkUrgency = "any",
  ) => {
    try {
      const workLabel =
        tile === "all_pending"
          ? ""
          : tile in MOCKUP_WORK_STATE_LABELS
            ? MOCKUP_WORK_STATE_LABELS[tile as MockupWorkState]
            : tile in FILE_WORK_STATE_LABELS
              ? FILE_WORK_STATE_LABELS[tile as FileWorkState]
              : tile in PRINT_IN_WORK_STATE_LABELS
                ? PRINT_IN_WORK_STATE_LABELS[tile as PrintInWorkState]
                : tile in PRINT_WORK_STATE_LABELS
                  ? PRINT_WORK_STATE_LABELS[tile as PrintWorkState]
                  : tile in RAW_WORK_STATE_LABELS
                    ? RAW_WORK_STATE_LABELS[tile as RawWorkState]
                    : tile in PO_WORK_STATE_LABELS
                      ? PO_WORK_STATE_LABELS[tile as PoWorkState]
                      : tile in PROJECT_WORK_STATE_LABELS
                        ? PROJECT_WORK_STATE_LABELS[tile as ProjectWorkState]
                        : PR_WORK_STATE_LABELS[tile as PrWorkState];
      const mockup_work_state =
        tile === "all_pending" ? [] : [{ id: tile, value: workLabel }];
      const match = MOCKUP_WORK_BUCKET_OPTIONS.find(
        (option) => option.id === urgency,
      );
      const work_bucket =
        urgency === "any" || !match
          ? []
          : [{ id: match.id, value: match.value }];

      const next: DailyTasksFilters = {
        ...this.filters,
        work_bucket,
        mockup_work_state,
        page_no: 1,
      };
      trackDailyTasksAction("work_tile_select", next, {
        workTileId: tile,
        workTile: tile === "all_pending" ? "All pending" : workLabel || tile,
        urgency,
        context: {
          work_tile: tile,
          urgency,
        },
      });
      await this.fetchQueue(next, { refreshCards: false });
    } catch (error) {
      throw error;
    }
  };

  changeQueuePage = async (pageNo: number, limit?: number) => {
    try {
      const previousLimit = this.filters.limit;
      const nextLimit = limit ?? this.filters.limit;
      const next: DailyTasksFilters = {
        ...this.filters,
        page_no: pageNo,
        limit: nextLimit,
      };
      if (limit != null && limit !== previousLimit) {
        trackDailyTasksAction("queue_page_size_change", next, {
          pageSize: nextLimit,
          previousPageSize: previousLimit,
          context: {
            page_size: nextLimit,
            previous_page_size: previousLimit,
          },
        });
      } else {
        trackDailyTasksAction("queue_page_change", next, {
          page: pageNo,
          pageSize: nextLimit,
          context: { page: pageNo, page_size: nextLimit },
        });
      }
      await this.fetchQueue(next, { refreshCards: false });
    } catch (error) {
      throw error;
    }
  };

  changeQueueSort = async (column: DailyTasksSortBy, sortType: SortType) => {
    try {
      const next: DailyTasksFilters = {
        ...this.filters,
        sort_by: column,
        sort_type: sortType,
        page_no: 1,
      };
      trackDailyTasksAction("queue_sort", next, {
        sortColumn: column,
        sortDir: sortType,
        context: { sort_column: column, sort_dir: sortType },
      });
      await this.fetchQueue(next, { refreshCards: false });
    } catch (error) {
      throw error;
    }
  };

  openDrawer = async (
    metricId: DailyTasksTatMetricId,
    options?: { cardTitle?: string; cardSection?: string },
  ) => {
    try {
      const cardTitle = options?.cardTitle?.trim() || null;
      const cardSection = options?.cardSection?.trim() || null;
      trackDailyTasksAction("drilldown_open", this.filters, {
        metricId,
        cardTitle: cardTitle || undefined,
        cardSection: cardSection || undefined,
        context: {
          metric_id: metricId,
          ...(cardTitle ? { card_title: cardTitle } : {}),
          ...(cardSection ? { card_section: cardSection } : {}),
        },
      });
      this.drawerOpen = true;
      this.drawerMetricId = metricId;
      this.drawerCardTitle = cardTitle;
      this.drawerCardSection = cardSection;
      this.drilldownQuery = {
        ...defaultDailyTasksTatDrilldownQuery(),
        record_scope: this.filters.include_running ? "all" : "measured",
      };
      this.drilldownResult = null;
      this.drilldownError = null;
      this.exportError = null;
      await this.fetchDrilldown();
    } catch (error) {
      throw error;
    }
  };

  /**
   * Shared GA extras for the currently open card (title + section + metric).
   *
   * @returns Label/context fields for trackDailyTasksAction, or empty when no card.
   */
  private drawerTrackExtra = () => {
    if (!this.drawerMetricId) {
      return {};
    }
    return {
      metricId: this.drawerMetricId,
      cardTitle: this.drawerCardTitle || undefined,
      cardSection: this.drawerCardSection || undefined,
      context: {
        metric_id: this.drawerMetricId,
        ...(this.drawerCardTitle
          ? { card_title: this.drawerCardTitle }
          : {}),
        ...(this.drawerCardSection
          ? { card_section: this.drawerCardSection }
          : {}),
      },
    };
  };

  closeDrawer = (options?: { silent?: boolean }) => {
    if (this.drawerOpen && !options?.silent) {
      const cardExtra = this.drawerTrackExtra();
      trackDailyTasksAction("drilldown_close", this.filters, {
        ...cardExtra,
        context: {
          ...cardExtra.context,
          viewing_product: Boolean(this.selectedRow),
        },
      });
    }
    this.drilldownRequestId += 1;
    this.drawerOpen = false;
    this.drawerMetricId = null;
    this.drawerCardTitle = null;
    this.drawerCardSection = null;
    this.drilldownResult = null;
    this.drilldownError = null;
    this.exportError = null;
    this.drilldownLoading = false;
    this.exportLoading = false;
    this.drilldownQuery = defaultDailyTasksTatDrilldownQuery();
    this.closeProductDrawer({ silent: true });
  };

  fetchDrilldown = async (overrides?: Partial<DailyTasksTatDrilldownQuery>) => {
    const requestId = ++this.drilldownRequestId;
    try {
      if (!this.drawerMetricId) {
        return;
      }

      if (overrides) {
        const prev = this.drilldownQuery;
        const cardExtra = this.drawerTrackExtra();
        if (
          overrides.record_scope !== undefined &&
          overrides.record_scope !== prev.record_scope
        ) {
          trackDailyTasksAction("drilldown_scope_change", this.filters, {
            ...cardExtra,
            recordScope: overrides.record_scope,
            context: {
              ...cardExtra.context,
              record_scope: overrides.record_scope,
            },
          });
        } else if (overrides.sort !== undefined) {
          const nextSort = overrides.sort;
          const prevSort = prev.sort;
          const sortChanged =
            !prevSort ||
            !nextSort ||
            prevSort.column !== nextSort.column ||
            prevSort.dir !== nextSort.dir;
          if (sortChanged) {
            trackDailyTasksAction("drilldown_sort", this.filters, {
              ...cardExtra,
              sortColumn: nextSort.column,
              sortDir: nextSort.dir,
              context: {
                ...cardExtra.context,
                sort_column: nextSort.column,
                sort_dir: nextSort.dir,
              },
            });
          }
        } else if (
          overrides.page_size !== undefined &&
          overrides.page_size !== prev.page_size
        ) {
          trackDailyTasksAction("drilldown_page_size_change", this.filters, {
            ...cardExtra,
            pageSize: overrides.page_size,
            previousPageSize: prev.page_size,
            context: {
              ...cardExtra.context,
              page_size: overrides.page_size,
              previous_page_size: prev.page_size,
            },
          });
        } else if (
          overrides.page !== undefined &&
          overrides.page !== prev.page
        ) {
          trackDailyTasksAction("drilldown_page_change", this.filters, {
            ...cardExtra,
            page: overrides.page,
            pageSize: prev.page_size,
            context: {
              ...cardExtra.context,
              page: overrides.page,
              page_size: prev.page_size,
            },
          });
        }
      }

      this.drilldownQuery = { ...this.drilldownQuery, ...overrides };
      this.drilldownLoading = true;
      this.drilldownError = null;

      const body = {
        ...toDailyTasksTatBody(this.filters),
        page: this.drilldownQuery.page,
        page_size: this.drilldownQuery.page_size,
        record_scope: this.drilldownQuery.record_scope,
        ...(this.drilldownQuery.sort ? { sort: this.drilldownQuery.sort } : {}),
      };

      const result = await this.dailyTasksAPI.getTatDrilldown(
        this.drawerMetricId,
        body,
      );

      if (requestId !== this.drilldownRequestId) return;
      runInAction(() => {
        this.drilldownResult = result;
      });
    } catch (error) {
      if (requestId !== this.drilldownRequestId) return;
      runInAction(() => {
        this.drilldownError = getApiErrorMessage(
          error,
          "Failed to fetch daily tasks TAT drilldown.",
        );
        this.drilldownResult = null;
      });
      throw error;
    } finally {
      if (requestId === this.drilldownRequestId) {
        runInAction(() => {
          this.drilldownLoading = false;
        });
      }
    }
  };

  exportCsv = async () => {
    try {
      if (!this.drawerMetricId) {
        return;
      }

      trackDailyTasksAction("export_csv", this.filters, {
        ...this.drawerTrackExtra(),
      });
      this.exportLoading = true;
      this.exportError = null;

      const result = await this.dailyTasksAPI.exportTatDrilldownCsv(
        this.drawerMetricId,
        toDailyTasksTatBody(this.filters),
        {
          record_scope: this.drilldownQuery.record_scope,
          sort: this.drilldownQuery.sort,
        },
      );

      if (!result.data) {
        throw new Error("Download failed");
      }

      triggerCsvDownload(
        result.data,
        result.filename || `daily-tasks-tat-${this.drawerMetricId}.csv`,
      );
    } catch (error) {
      runInAction(() => {
        this.exportError = getApiErrorMessage(error, "Failed to export CSV.");
      });
      throw error;
    } finally {
      runInAction(() => {
        this.exportLoading = false;
      });
    }
  };

  openProductDrawer = (
    orderId: number,
    productId: number,
    row?: DailyTasksListRow,
  ) => {
    trackDailyTasksAction("product_drawer_open", this.filters, {
      orderId,
      productId,
      context: { order_id: orderId, product_id: productId },
    });
    this.selectedRow = row ?? stubListRow(orderId, productId);
    this.detail = null;
    this.drawerErrorMessage = null;
    if (!this.drawerOpen) {
      this.productDrawerOpen = true;
    }
  };

  closeProductDrawer = (options?: { silent?: boolean }) => {
    if (
      !options?.silent &&
      (this.productDrawerOpen || this.selectedRow != null)
    ) {
      trackDailyTasksAction("product_drawer_close", this.filters, {
        orderId: this.selectedRow?.order_id,
        productId: this.selectedRow?.product_id,
        context: {
          order_id: this.selectedRow?.order_id,
          product_id: this.selectedRow?.product_id,
        },
      });
    }
    this.detailRequestId += 1;
    this.productDrawerOpen = false;
    this.selectedRow = null;
    this.detail = null;
    this.drawerLoading = false;
    this.drawerErrorMessage = null;
  };

  fetchDetail = async () => {
    if (!this.selectedRow) return;
    const requestId = ++this.detailRequestId;
    try {
      this.drawerLoading = true;
      this.drawerErrorMessage = null;
      const detail = await this.dailyTasksAPI.getDetail(
        this.selectedRow.order_id,
        this.selectedRow.product_id,
        this.filters.stage,
      );
      if (requestId !== this.detailRequestId) return;
      runInAction(() => {
        this.detail = detail;
      });
    } catch (error) {
      if (requestId !== this.detailRequestId) return;
      console.error(error);
      runInAction(() => {
        this.drawerErrorMessage = getApiErrorMessage(
          error,
          "Failed to fetch task details.",
        );
      });
      throw error;
    } finally {
      if (requestId === this.detailRequestId) {
        runInAction(() => {
          this.drawerLoading = false;
        });
      }
    }
  };
}
